/* PocDrawer — POC slide-over (view / edit / add) used from the Partner 360
   Overview POC table + org tree. React port of mockup openPocDrawer():
   · view mode — full dl + Edit / Deactivate-Activate (confirm with reason)
   · add/edit mode — field grid (name/desig | email/m1 | m2/linkedin | notes)
     plus role checkboxes, with the mockup pocRulesFor() validation. */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import CheckIcon from '@mui/icons-material/Check'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {SideDrawer, ConfirmDialog, Badge} from '../components'
import {apiError} from '../supplyMeta'
import {useSaveContactsMutation} from '../../../redux/supplyApiSlice'
import {toBackendContact} from './mapping'

export const POC_ROLES = [
  'Key Account Manager (KAM)',
  'Account Head',
  'Business Head / VP',
  'Weight Discrepancy POC',
  'COD Remittance POC',
  'Performance Escalation POC',
  'Technical Integration POC',
  'Finance / Billing POC',
]

const digits = (v) => String(v || '').replace(/\D/g, '')
const phone10Ok = (v) => {
  const d = digits(v)
  return d.length === 10 || (d.length === 12 && d.startsWith('91'))
}

const blankDraft = (prefRole) => {
  const role = prefRole ? POC_ROLES.find((r) => r.startsWith(prefRole)) || prefRole : null
  return {
    name: '',
    desig: '',
    roles: role ? [role] : [],
    email: '',
    m1: '',
    m2: '',
    linkedin: '',
    notes: '',
    status: 'Active',
  }
}

/* mockup pocRulesFor() — name/email/m1/m2 required, m2 ≠ m1, linkedin URL, notes ≤ 300 */
function validate(d) {
  const errs = {}
  if (!d.name || d.name.trim().length < 2) errs.name = 'Required.'
  if (!d.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) errs.email = 'Enter a valid email.'
  if (!phone10Ok(d.m1)) errs.m1 = 'Enter a valid 10-digit mobile.'
  if (!phone10Ok(d.m2)) errs.m2 = 'Enter a valid 10-digit mobile.'
  else if (digits(d.m2) && digits(d.m2) === digits(d.m1)) errs.m2 = 'Mobile 2 must differ from Mobile 1.'
  if (d.linkedin && !/^https?:\/\/([\w-]+\.)?linkedin\.com\//i.test(d.linkedin))
    errs.linkedin = 'Enter a valid linkedin.com profile URL.'
  if (d.notes && d.notes.length > 300) errs.notes = 'Notes must be 300 characters or fewer.'
  if (!d.roles || !d.roles.length) errs.roles = 'Select at least one role.'
  return errs
}

/* one .field — input/textarea + vmsg (+ optional help) so the scoped CSS applies */
function Fld({label, k, draft, setDraft, errs, opt, full, type, ph, help}) {
  const err = errs[k]
  return (
    <div
      className={'field' + (err ? ' invalid' : '')}
      style={full ? {gridColumn: '1/-1'} : undefined}
    >
      <label>
        {label}{' '}
        {opt ? <span className='opt'>(optional)</span> : <span className='req'>*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          className='input'
          rows={2}
          placeholder={ph || ''}
          value={draft[k] || ''}
          onChange={(e) => setDraft({...draft, [k]: e.target.value})}
        />
      ) : (
        <input
          className='input'
          placeholder={ph || label}
          value={draft[k] || ''}
          onChange={(e) => setDraft({...draft, [k]: e.target.value})}
        />
      )}
      {help ? (
        <div className='help'>
          <InfoOutlinedIcon style={{fontSize: 13}} />
          {help}
        </div>
      ) : null}
      <div className='vmsg'>{err || 'Required.'}</div>
    </div>
  )
}

Fld.propTypes = {
  label: PropTypes.string,
  k: PropTypes.string,
  draft: PropTypes.object,
  setDraft: PropTypes.func,
  errs: PropTypes.object,
  opt: PropTypes.bool,
  full: PropTypes.bool,
  type: PropTypes.string,
  ph: PropTypes.string,
  help: PropTypes.string,
}

function Dl({k, children}) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{children}</dd>
    </>
  )
}

Dl.propTypes = {k: PropTypes.node, children: PropTypes.node}

/* state: {open, mode:'view'|'edit'|'add', idx, prefRole} */
export default function PocDrawer({p, state, onClose, onChanged, toast}) {
  const {open, idx, prefRole} = state
  const isAdd = state.mode === 'add' || idx == null
  const [mode, setMode] = useState('view')
  const [draft, setDraft] = useState(null)
  const [errs, setErrs] = useState({})
  const [confirmOff, setConfirmOff] = useState(false)
  const [saving, setSaving] = useState(false)
  const [doSaveContacts] = useSaveContactsMutation()

  useEffect(() => {
    if (!open) return
    setMode(state.mode || (idx == null ? 'add' : 'view'))
    const src = idx != null && p ? (p.pocs || [])[idx] : null
    setDraft(src ? {...src, roles: [...(src.roles || [])]} : blankDraft(prefRole))
    setErrs({})
    setConfirmOff(false)
    setSaving(false)
  }, [open, state.mode, idx, prefRole, p])

  if (!p || !draft) return null
  const poc = idx != null ? (p.pocs || [])[idx] : null
  const inactive = poc && poc.status && poc.status !== 'Active'
  const isForm = mode === 'add' || mode === 'edit'

  const toggleRole = (r) => {
    const roles = draft.roles.includes(r)
      ? draft.roles.filter((x) => x !== r)
      : [...draft.roles, r]
    setDraft({...draft, roles})
    if (errs.roles) setErrs({...errs, roles: null})
  }

  /* FULL-REPLACE save: existing contacts with edits applied + new contact
     (contactId null) appended, mapped to backend enums via toBackendContact.
     Server enforces min 3 POCs, mandatory role coverage and distinct 10-digit
     mobiles — surfaced via apiError toast. */
  const saveContacts = (nextPocs, successMsg) => {
    setSaving(true)
    doSaveContacts({id: p.id, contacts: nextPocs.map(toBackendContact)})
      .unwrap()
      .then(() => {
        toast(successMsg, 'success')
        onClose()
        onChanged()
      })
      .catch((e) => toast(apiError(e), 'error'))
      .finally(() => setSaving(false))
  }

  const save = () => {
    const e = validate(draft)
    const bad = Object.keys(e).filter((k) => e[k])
    setErrs(e)
    if (bad.length) {
      toast('Fix the highlighted fields before saving.', 'error')
      return
    }
    const pocs = (p.pocs || []).slice()
    if (isAdd) {
      pocs.push({...draft, contactId: null, status: draft.status || 'Active'})
      saveContacts(pocs, 'POC added to ' + p.name + '.')
    } else {
      pocs[idx] = {...pocs[idx], ...draft}
      saveContacts(pocs, 'POC updated.')
    }
  }

  const setPocActive = (active, reason) => {
    const pocs = (p.pocs || []).map((c, i) =>
      i === idx
        ? {
            ...c,
            status: active ? 'Active' : 'Inactive',
            notes: !active && reason ? ((c.notes ? c.notes + ' · ' : '') + 'Deactivated: ' + reason).slice(0, 300) : c.notes,
          }
        : c
    )
    saveContacts(pocs, active ? 'POC reactivated.' : 'POC deactivated. Audit recorded.')
  }

  const togglePocStatus = () => {
    if (inactive) setPocActive(true)
    else setConfirmOff(true)
  }

  /* ---- form (add / edit) ---- */
  const formBody = (
    <>
      <div className='grid' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
        <Fld
          label='Full name'
          k='name'
          draft={draft}
          setDraft={setDraft}
          errs={errs}
          help='First + last name of the POC. Use the name as it appears on their email signature.'
        />
        <Fld label='Designation' k='desig' draft={draft} setDraft={setDraft} errs={errs} ph='Designation' />
        <Fld label='Primary email' k='email' draft={draft} setDraft={setDraft} errs={errs} ph='name@courier.com' />
        <Fld label='Mobile 1' k='m1' draft={draft} setDraft={setDraft} errs={errs} ph='10-digit mobile' />
        <Fld label='Mobile 2' k='m2' draft={draft} setDraft={setDraft} errs={errs} ph='10-digit mobile' />
        <Fld label='LinkedIn' k='linkedin' draft={draft} setDraft={setDraft} errs={errs} opt ph='https://linkedin.com/in/…' />
      </div>
      <Fld
        label='Notes'
        k='notes'
        draft={draft}
        setDraft={setDraft}
        errs={errs}
        opt
        full
        type='textarea'
        ph='e.g. Prefers WhatsApp. Available Mon–Sat 9am–6pm.'
      />
      <div className={'field' + (errs.roles ? ' invalid' : '')} style={{marginTop: 12}}>
        <label>
          Role(s) <span className='req'>*</span>
        </label>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
          {POC_ROLES.map((r) => (
            <label className='chip' key={r} style={{cursor: 'pointer'}}>
              <input
                type='checkbox'
                checked={draft.roles.includes(r)}
                onChange={() => toggleRole(r)}
                style={{marginRight: 5}}
              />
              {r.replace(/ \(.+\)/, '')}
            </label>
          ))}
        </div>
        <div className='vmsg'>Select at least one role.</div>
      </div>
    </>
  )

  /* ---- view ---- */
  const viewBody = poc ? (
    <dl className='dl' style={{gridTemplateColumns: 'auto 1fr'}}>
      <Dl k='Name'>
        <b>{poc.name}</b>
      </Dl>
      <Dl k='Designation'>{poc.desig || '—'}</Dl>
      <Dl k='Roles'>
        {(poc.roles || []).length ? (
          (poc.roles || []).map((r) => (
            <span className='tag' key={r} style={{marginRight: 5}}>
              {r.replace(/ \(.+\)/, '')}
            </span>
          ))
        ) : (
          <span className='muted'>No roles</span>
        )}
      </Dl>
      <Dl k='Email'>
        <a className='lnk' href={'mailto:' + poc.email}>
          {poc.email}
        </a>
      </Dl>
      <Dl k='Mobile 1'>
        <span className='mono'>{poc.m1 || '—'}</span>
      </Dl>
      <Dl k='Mobile 2'>
        <span className='mono'>{poc.m2 || '—'}</span>
      </Dl>
      <Dl k='LinkedIn'>
        {poc.linkedin ? (
          <a className='lnk' href={poc.linkedin} target='_blank' rel='noreferrer'>
            {poc.linkedin} <OpenInNewOutlinedIcon style={{fontSize: 12, verticalAlign: '-2px'}} />
          </a>
        ) : (
          <span className='muted'>—</span>
        )}
      </Dl>
      <Dl k='Notes'>{poc.notes ? poc.notes : <span className='muted'>—</span>}</Dl>
      <Dl k='Status'>
        <Badge status={inactive ? 'inactive' : 'active'} />
      </Dl>
    </dl>
  ) : null

  const formFoot = (
    <>
      <button className='btn' onClick={onClose}>
        Cancel
      </button>
      <button className='btn btn-primary' onClick={save} disabled={saving}>
        <CheckIcon style={{fontSize: 16}} />
        {saving ? 'Saving…' : isAdd ? 'Add POC' : 'Save Changes'}
      </button>
    </>
  )

  const viewFoot = (
    <>
      <button className='btn' onClick={onClose}>
        Close
      </button>
      <button className='btn' onClick={() => setMode('edit')}>
        <EditOutlinedIcon style={{fontSize: 16}} />
        Edit
      </button>
      <button
        className={'btn ' + (inactive ? 'btn-primary' : 'btn-danger')}
        onClick={togglePocStatus}
        disabled={saving}
      >
        <PowerSettingsNewOutlinedIcon style={{fontSize: 16}} />
        {inactive ? 'Activate POC' : 'Deactivate POC'}
      </button>
    </>
  )

  return (
    <>
      <SideDrawer
        open={open}
        onClose={onClose}
        width={isForm ? 560 : 520}
        title={
          isForm ? (isAdd ? 'Add POC — ' : 'Edit POC — ') + p.name : 'POC · ' + (poc ? poc.name : '')
        }
        footer={isForm ? formFoot : viewFoot}
      >
        {isForm ? formBody : viewBody}
      </SideDrawer>
      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        title={poc ? 'Deactivate ' + poc.name + '?' : ''}
        danger
        needReason
        body={poc ? 'This POC will be marked inactive on ' + p.name + '.' : ''}
        onOk={(reason) => {
          setConfirmOff(false)
          setPocActive(false, reason)
        }}
      />
    </>
  )
}

PocDrawer.propTypes = {
  p: PropTypes.object,
  state: PropTypes.shape({
    open: PropTypes.bool,
    mode: PropTypes.oneOf(['view', 'edit', 'add']),
    idx: PropTypes.number,
    prefRole: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  toast: PropTypes.func.isRequired,
}
